import { Capacitor, registerPlugin } from '@capacitor/core';
import { appeler } from './callable';

/**
 * Etre alle quelque part, et le faire savoir.
 *
 * # La regle du jeu
 *
 * Le telephone n'envoie qu'un point : latitude, longitude, precision. Il
 * ne sait pas ou sont les lieux d'un trophee, et n'apprend qu'apres coup
 * ce que son point lui a rapporte. Toute la comparaison se fait sur le
 * serveur, ou personne ne peut ecrire — un trophee qu'on peut se donner
 * ne vaut rien.
 *
 * # Un point, pas un suivi
 *
 * Aucun releve continu, rien en arriere-plan. L'autorisation demandee est
 * « pendant l'utilisation », et elle est tenue : la position n'est lue
 * qu'a l'ouverture de l'onglet Trophees. La contrepartie doit etre dite a
 * qui l'utilise — un trophee de lieu se decroche **en ouvrant
 * l'application sur place**.
 *
 * # Sur le web, rien
 *
 * `navigator.geolocation` existerait, mais le navigateur n'est pas ce
 * qu'on emporte en promenade. Tout est garde par `isNativePlatform`.
 */

const Position = registerPlugin<{
  etat(): Promise<{ etat: EtatPosition }>;
  demander(): Promise<{ etat: EtatPosition }>;
  ouSuisJe(): Promise<{ lat: number; lon: number; precision: number }>;
  suivre(): Promise<{ actif: boolean }>;
  cesserDeSuivre(): Promise<{ actif: boolean }>;
  chemin(): Promise<{ actif: boolean; segments: Segment[] }>;
  etatSuivi(): Promise<{ actif: boolean; enAttente: number }>;
  ouvrirLesReglages(): Promise<{ ouvert: boolean }>;
}>('Position');

/** Un morceau de chemin mesure par le telephone. */
interface Segment {
  lat: number;
  lon: number;
  metres: number;
  at: number;
}

export type EtatPosition = 'a-demander' | 'accorde' | 'refuse';

/** Ce que le passage a rapporte, tel que le serveur le rend. */
export interface Passage {
  /** Lieux nouvellement retenus. */
  retenus: number;
  /** Trophees debloques par ce point. */
  debloques: string[];
}

function natif(): boolean {
  return Capacitor.isNativePlatform();
}

/** Ou en est l'autorisation, sans rien demander. */
export async function etatPosition(): Promise<EtatPosition> {
  if (!natif()) return 'refuse';
  return (await Position.etat()).etat;
}

/** Demande l'autorisation. Une seule fois : iOS ne represente pas la question. */
export async function demanderPosition(): Promise<EtatPosition> {
  if (!natif()) return 'refuse';
  return (await Position.demander()).etat;
}

/**
 * Ouvre la fiche de l'application dans les reglages du telephone.
 *
 * C'est la seule facon de revenir sur un refus : iOS ne pose sa question
 * qu'une fois, et Android cesse de la poser apres deux refus. Sans cette
 * porte, l'ecran ne peut qu'en donner le chemin par ecrit — des pas a
 * suivre de memoire, dans une liste ou se retrouver.
 *
 * Sur le web il n'y a pas de fiche a ouvrir : l'autorisation se reprend
 * dans le cadenas de la barre d'adresse, et la fonction rend `false`
 * pour que l'ecran le dise plutot que de promettre un geste qui n'aura
 * pas lieu.
 */
export async function ouvrirLesReglages(): Promise<boolean> {
  if (!natif()) return false;
  try {
    return (await Position.ouvrirLesReglages()).ouvert;
  } catch {
    return false;
  }
}

/**
 * Releve un point et l'envoie au serveur.
 *
 * Rend `null` quand il n'y a rien a dire — pas d'autorisation, pas de
 * telephone, releve impossible. Un echec ici ne doit jamais remonter a
 * l'ecran : on regarde ses trophees, on ne fait pas un releve.
 */
export async function signalerOuJeSuis(): Promise<Passage | null> {
  if (!natif()) return null;
  if ((await etatPosition()) !== 'accorde') return null;

  try {
    const { lat, lon, precision } = await Position.ouSuisJe();
    return await appeler<{ lat: number; lon: number; precision: number }, Passage>(
      'signalerPosition',
      { lat, lon, precision },
    );
  } catch {
    return null;
  }
}

/* --- Le compteur de kilometres ---------------------------------------- */

/**
 * Le suivi de chemin, vu de la page.
 *
 * Le telephone mesure pendant qu'on marche, application fermee, et empile
 * ses morceaux hors ligne. La page vient les chercher quand elle peut et
 * les remet au serveur, qui seul decide de ce qu'ils valent.
 *
 * L'envoi vide le tampon : un morceau compte une fois. Ce qui echoue en
 * chemin est perdu — un kilometre perdu vaut mieux qu'un kilometre compte
 * deux fois.
 */
export interface Chemin {
  /** Metres nouvellement retenus par le serveur. */
  ajoutes: number;
  /** Trophees debloques par cet envoi. */
  debloques: string[];
}

/**
 * Le compteur tourne-t-il ?
 *
 * Par `etatSuivi` et non par `chemin` : ce dernier vide le tampon en le
 * rendant, et demander l'etat n'est pas venir chercher. La premiere
 * version le faisait, et perdait le chemin a chaque ouverture de
 * l'onglet.
 */
export async function suiviActif(): Promise<boolean> {
  if (!natif()) return false;
  try {
    return (await Position.etatSuivi()).actif;
  } catch {
    return false;
  }
}

/** Allume le compteur. Demande l'autorisation si elle n'a jamais ete posee. */
export async function demarrerSuivi(): Promise<boolean> {
  if (!natif()) return false;
  if ((await etatPosition()) !== 'accorde' && (await demanderPosition()) !== 'accorde') return false;
  try {
    return (await Position.suivre()).actif;
  } catch {
    return false;
  }
}

/** Eteint le compteur. Le chemin deja mesure reste a envoyer. */
export async function arreterSuivi(): Promise<boolean> {
  if (!natif()) return false;
  try {
    await Position.cesserDeSuivre();
    return false;
  } catch {
    return false;
  }
}

/**
 * Remet au serveur le chemin mesure depuis la derniere fois.
 *
 * Rend `null` quand il n'y a rien a dire. Un echec ne remonte jamais a
 * l'ecran : on regarde ses trophees, on ne televerse pas une trace.
 */
export async function envoyerLeChemin(): Promise<Chemin | null> {
  if (!natif()) return null;

  try {
    const { segments } = await Position.chemin();
    if (!segments?.length) return null;
    return await appeler<{ segments: Segment[] }, Chemin>('signalerChemin', { segments });
  } catch {
    return null;
  }
}

/* --- Ou je suis, pour moi seul ---------------------------------------- */

/**
 * Le point de l'appareil, qui ne quitte pas l'appareil.
 *
 * A part de `signalerOuJeSuis`, qui remet le point au serveur pour qu'il
 * en tire des trophees. Ici rien ne part : le filtre de distance compare
 * en memoire, dans la page, et le point est oublie en quittant l'onglet.
 *
 * # Le web y a droit, contrairement au reste de ce fichier
 *
 * Le refus plus haut vise l'arpentage : un navigateur n'est pas ce qu'on
 * emporte en promenade, et un trophee de lieu decroche depuis un bureau
 * ne vaudrait rien. Demander « les sorties a moins de 10 km de moi »
 * reste une question legitime depuis un ordinateur, et le navigateur sait
 * y repondre — avec sa propre demande d'autorisation, la meme qu'ailleurs.
 *
 * Rend `null` des que l'on ne peut pas savoir : autorisation refusee,
 * releve impossible, delai depasse. L'ecran le dit alors plutot que de
 * filtrer sur un point invente.
 */
export async function monPoint(): Promise<{ lat: number; lon: number } | null> {
  if (natif()) {
    /* On demande si la question n'a jamais ete posee : choisir un rayon
       autour de soi, c'est demander a etre localise. */
    if ((await etatPosition()) !== 'accorde' && (await demanderEtAttendre()) !== 'accorde') {
      return null;
    }
    /*
      Un natif qui ne repond pas, la page prend le relais.

      Jusqu'a la coquille 19, le gestionnaire natif naissait sur une file
      sans boucle et ne repondait jamais : la demande restait pendante pour
      toujours. La page sait aussi demander la position, par WebKit, qui
      tient sa propre boucle. Passe six secondes sans reponse, on lui
      laisse la main — la coquille corrigee repond bien avant.
    */
    const MUET = Symbol('muet');
    try {
      const reponse = await Promise.race([
        Position.ouSuisJe(),
        new Promise<typeof MUET>((resoudre) => window.setTimeout(() => resoudre(MUET), 6000)),
      ]);
      if (reponse !== MUET) return { lat: reponse.lat, lon: reponse.lon };
    } catch {
      return null;
    }
  }

  return pointDuNavigateur();
}

/**
 * La question d'autorisation, et sa reponse — meme si le natif la tait.
 *
 * Le meme defaut que plus haut : sur une coquille d'avant la 19, la
 * reponse du membre n'arrivait jamais jusqu'ici. On relit donc l'etat
 * chaque seconde, une minute au plus : il change des qu'il a repondu.
 */
async function demanderEtAttendre(): Promise<EtatPosition> {
  const demande = demanderPosition().catch(() => 'refuse' as const);
  for (let seconde = 0; seconde < 60; seconde += 1) {
    const fini = await Promise.race([
      demande,
      new Promise<null>((resoudre) => window.setTimeout(() => resoudre(null), 1000)),
    ]);
    if (fini) return fini;
    const etat = await etatPosition().catch(() => 'refuse' as const);
    if (etat !== 'a-demander') return etat;
  }
  return 'refuse';
}

/** La position demandee par la page : le web, et le secours d'un natif muet. */
function pointDuNavigateur(): Promise<{ lat: number; lon: number } | null> {
  if (!navigator.geolocation) return Promise.resolve(null);
  return new Promise((resoudre) => {
    navigator.geolocation.getCurrentPosition(
      (releve) => resoudre({ lat: releve.coords.latitude, lon: releve.coords.longitude }),
      () => resoudre(null),
      {
        /*
          La precision fine coute une attente et de la batterie pour
          gagner quelques metres — sans objet quand la question se pose
          en kilometres. Un releve vieux de cinq minutes fait tres bien
          l'affaire, et evite de rallumer la puce a chaque ouverture.
        */
        enableHighAccuracy: false,
        timeout: 10_000,
        maximumAge: 300_000,
      },
    );
  });
}
